
import React from "react";
import { Separator } from "@/components/ui/separator";
import { Beaker, Thermometer, Calendar, MapPin, Clock, Package } from "lucide-react";
import { Reagent } from "@/types/reagent.types";
import { formatDate } from "@/utils/dateUtils";

interface ReagentInfoSectionProps {
  reagent: Reagent;
}

const ReagentInfoSection: React.FC<ReagentInfoSectionProps> = ({ reagent }) => {
  const formatReagentAmount = (amount: string | number | null, unit: string | null) => {
    if (amount === null) return "Not specified";
    return `${amount} ${unit || ''}`;
  };

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold mb-2">{reagent.name}</h1>
      <p className="text-gray-600 mb-4">Catalog #: {reagent.catalogNumber || reagent.catalogue_number || "N/A"}</p>
      
      <Separator className="my-6" />
      
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold mb-2">Description</h2>
          <p className="text-gray-700">{reagent.description || "No description available."}</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start">
            <div className="h-8 w-8 rounded-full bg-reagent-light-blue flex items-center justify-center mt-1 mr-3">
              <Beaker className="h-5 w-5 text-reagent-blue" />
            </div>
            <div>
              <h3 className="font-medium">Amount</h3>
              <p>{formatReagentAmount(reagent.amount, reagent.amount_unit)}</p>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="h-8 w-8 rounded-full bg-reagent-light-blue flex items-center justify-center mt-1 mr-3">
              <Package className="h-5 w-5 text-reagent-blue" />
            </div>
            <div>
              <h3 className="font-medium">Nr of packages/containers</h3>
              <p>{reagent.numberOfPackages || reagent.number_of_packages || "Not specified"}</p>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="h-8 w-8 rounded-full bg-reagent-light-blue flex items-center justify-center mt-1 mr-3">
              <Thermometer className="h-5 w-5 text-reagent-blue" />
            </div>
            <div>
              <h3 className="font-medium">Storage Conditions</h3>
              <p>{reagent.storageConditions || reagent.storage_conditions || "Not specified"}</p>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="h-8 w-8 rounded-full bg-reagent-light-blue flex items-center justify-center mt-1 mr-3">
              <Calendar className="h-5 w-5 text-reagent-blue" />
            </div>
            <div>
              <h3 className="font-medium">Date Added</h3>
              <p>{(reagent.dateAdded || reagent.created_at) ? formatDate(reagent.dateAdded || reagent.created_at) : "Unknown"}</p>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="h-8 w-8 rounded-full bg-reagent-light-blue flex items-center justify-center mt-1 mr-3">
              <Clock className="h-5 w-5 text-reagent-blue" />
            </div>
            <div>
              <h3 className="font-medium">Expiry Date</h3>
              <p>{(reagent.expiryDate || reagent.expiry_date) ? formatDate(reagent.expiryDate || reagent.expiry_date) : "Not specified"}</p>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="h-8 w-8 rounded-full bg-reagent-light-blue flex items-center justify-center mt-1 mr-3">
              <MapPin className="h-5 w-5 text-reagent-blue" />
            </div>
            <div>
              <h3 className="font-medium">Location</h3>
              <p>{(reagent.profiles && reagent.profiles.location) || 
                 `${reagent.city || ""}, ${reagent.country || ""}`.trim() || 
                 "Not specified"}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReagentInfoSection;
